import { api } from './api';

export interface TutorStructureNode {
  id: number;
  nodeType: 'TOPIC_REF' | 'CUSTOM_TOPIC' | 'WRAPPER';
  label: string;
  description?: string | null;
  displayOrder?: number;
  parentId?: number | null;
  refId?: number | null;
  isLibraryLinked: boolean;
  children: TutorStructureNode[];
}

export interface TutorStructure {
  courseId: number;
  viewId: number;
  nodes: TutorStructureNode[];
}

export interface TutorAddNodePayload {
  parentId?: number | null;
  nodeType: 'TOPIC_REF' | 'CUSTOM_TOPIC' | 'WRAPPER';
  refId?: number | null;
  label?: string;
  displayOrder?: number;
}

export interface TutorUpdateNodePayload {
  label?: string | null;
  description?: string | null;
}

export interface TutorMoveNodePayload {
  newParentId?: number | null;
  newIndex: number;
}

export interface TutorLibraryTopic {
  topicId: number;
  title: string;
  description?: string | null;
  distance: number;
}

export const getStructure = async (courseId: number): Promise<TutorStructure> => {
  const response = await api.get(`/api/tutor/courses/${courseId}/structure`);
  return response.data.data;
};

export const addNode = async (courseId: number, payload: TutorAddNodePayload): Promise<TutorStructureNode> => {
  const response = await api.post(`/api/tutor/courses/${courseId}/structure/nodes`, payload);
  return response.data.data;
};

export const updateNode = async (
  courseId: number,
  nodeId: number,
  payload: TutorUpdateNodePayload
): Promise<TutorStructureNode> => {
  const response = await api.patch(`/api/tutor/courses/${courseId}/structure/nodes/${nodeId}`, payload);
  return response.data.data;
};

export const deleteNode = async (
  courseId: number,
  nodeId: number,
  confirmCascade?: boolean
): Promise<void> => {
  await api.delete(`/api/tutor/courses/${courseId}/structure/nodes/${nodeId}`, {
    data: confirmCascade ? { confirmCascade: true } : undefined,
  });
};

export const moveNode = async (
  courseId: number,
  nodeId: number,
  payload: TutorMoveNodePayload
): Promise<void> => {
  await api.post(`/api/tutor/courses/${courseId}/structure/nodes/${nodeId}/move`, payload);
};

export const searchLibrary = async (
  courseId: number,
  query: string,
  limit = 20
): Promise<TutorLibraryTopic[]> => {
  const response = await api.get(`/api/tutor/courses/${courseId}/library/search`, {
    params: { q: query, limit },
  });
  return response.data.data;
};

// ─── Version management (Spec 027) ───────────────────────────────────────────

export interface TutorVersion {
  id: number;
  versionLabel: string;
  createdAt: string;
  isActive: boolean;
}

export const publishVersion = async (courseId: number): Promise<TutorVersion> => {
  const response = await api.post(`/api/tutor/courses/${courseId}/structure/publish`);
  return response.data.data;
};

export const getVersions = async (courseId: number): Promise<TutorVersion[]> => {
  const response = await api.get(`/api/tutor/courses/${courseId}/structure/versions`);
  return response.data.data;
};

export const activateVersion = async (courseId: number, versionId: number): Promise<void> => {
  await api.post(`/api/tutor/courses/${courseId}/structure/versions/${versionId}/activate`);
};

export const deleteVersion = async (courseId: number, versionId: number): Promise<void> => {
  await api.delete(`/api/tutor/courses/${courseId}/structure/versions/${versionId}`);
};

// ─── AI Structure (Spec 028) ──────────────────────────────────────────────────

export interface AiProposalNodeDTO {
  tempId: string;
  title: string;
  description?: string | null;
  parentTempId?: string | null;
  displayOrder: number;
}

export interface AiStructureProposalDTO {
  explanation: string;
  nodes: AiProposalNodeDTO[];
}

export const generateAiStructure = async (courseId: number): Promise<AiStructureProposalDTO> => {
  const response = await api.post(`/api/tutor/courses/${courseId}/structure/ai-suggest`);
  return response.data.data;
};

export const applyAiStructure = async (
  courseId: number,
  payload: { mode: 'additive' | 'replace'; nodes: AiProposalNodeDTO[] }
): Promise<{ appliedCount: number }> => {
  const response = await api.post(`/api/tutor/courses/${courseId}/structure/ai-apply`, payload);
  return response.data.data;
};
